import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Save, KeyRound, Mail, Loader2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const METHODS = ["saweria", "qris", "dana", "ovo", "gopay", "bank"];

export default function AdminSettings() {
  const { reload } = useSettings();
  const { refresh: refreshAuth } = useAuth();
  const [settings, setSettings] = useState(null);
  const [creds, setCreds] = useState({ current_password: "", new_email: "", new_password: "" });
  const [testingEmail, setTestingEmail] = useState(false);
  const qrInputs = useRef({});

  const load = async () => {
    try { const { data } = await api.get("/admin/settings"); setSettings(data); }
    catch (e) { console.error("Failed to load settings:", e); toast.error("Failed to load settings"); }
  };
  useEffect(() => { load(); }, []);

  if (!settings) return <div className="text-zinc-500 font-mystic">Loading…</div>;

  const save = async () => {
    try {
      await api.put("/admin/settings", settings);
      await reload();
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadQR = async (method, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({
        ...s,
        payment_methods: { ...s.payment_methods, [method]: { ...s.payment_methods[method], qr_image_url: data.url } },
      }));
      toast.success("QR image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setMethod = (key, field, value) => {
    setSettings((s) => ({ ...s, payment_methods: { ...s.payment_methods, [key]: { ...s.payment_methods[key], [field]: value } } }));
  };

  const updateCredentials = async () => {
    try {
      const body = { current_password: creds.current_password };
      if (creds.new_email) body.new_email = creds.new_email;
      if (creds.new_password) body.new_password = creds.new_password;
      await api.put("/admin/credentials", body);
      toast.success("Credentials updated");
      setCreds({ current_password: "", new_email: "", new_password: "" });
      await refreshAuth();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/admin/test-email");
      if (data.ok) toast.success(`Test email sent to ${data.sent_to}`);
      else toast.error(data.error || "Failed to send");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTestingEmail(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white">Site Settings</h2>
        <Button onClick={save} className="bg-rose-600 hover:bg-rose-500" data-testid="settings-save-btn"><Save className="w-4 h-4 mr-2" />Save All</Button>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="bg-zinc-950 border border-white/10">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="settings-tab-pricing">Membership</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments">Payment Methods</TabsTrigger>
          <TabsTrigger value="email" data-testid="settings-tab-email">Email (SMTP)</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Admin Credentials</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-5">
          <div className="glass-strong rounded-xl p-5 space-y-4 max-w-2xl">
            <Field label="Site Name" value={settings.site_name} onChange={(v) => setSettings({ ...settings, site_name: v })} testid="setting-site-name" />
            <Field label="Tagline" value={settings.tagline} onChange={(v) => setSettings({ ...settings, tagline: v })} testid="setting-tagline" />
            <Field label="Contact Email" value={settings.contact_email} onChange={(v) => setSettings({ ...settings, contact_email: v })} testid="setting-contact-email" />
            <div>
              <Label className="text-xs text-zinc-400">About Text</Label>
              <Textarea rows={5} value={settings.about_text || ""} onChange={(e) => setSettings({ ...settings, about_text: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="setting-about" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="mt-5">
          <div className="glass-strong rounded-xl p-5 space-y-4 max-w-md">
            <Field label="Elite Cultist Price (Rp)" type="number" value={settings.elite_price} onChange={(v) => setSettings({ ...settings, elite_price: parseInt(v) || 0 })} testid="setting-elite-price" />
            <Field label="Hardcore Cultist Price (Rp)" type="number" value={settings.hardcore_price} onChange={(v) => setSettings({ ...settings, hardcore_price: parseInt(v) || 0 })} testid="setting-hardcore-price" />
            <Field label="God Tier Supporter Price (Rp)" type="number" value={settings.god_tier_price} onChange={(v) => setSettings({ ...settings, god_tier_price: parseInt(v) || 0 })} testid="setting-god-tier-price" />
            <Field label="Membership Duration (days)" type="number" value={settings.membership_days} onChange={(v) => setSettings({ ...settings, membership_days: parseInt(v) || 30 })} testid="setting-membership-days" />

            <div className="border-t border-white/10 pt-4">
              <div className="font-display text-sm font-bold text-white mb-3">Community Pages</div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm text-white">Supporter Hall</div>
                  <div className="text-xs text-zinc-500">Public page listing God Tier supporters</div>
                </div>
                <Switch
                  checked={settings.supporter_hall_enabled !== false}
                  onCheckedChange={(v) => setSettings({ ...settings, supporter_hall_enabled: v })}
                  data-testid="setting-supporter-hall"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm text-white">Supporter Leaderboards</div>
                  <div className="text-xs text-zinc-500">Monthly & all-time leaderboards</div>
                </div>
                <Switch
                  checked={settings.leaderboard_enabled !== false}
                  onCheckedChange={(v) => setSettings({ ...settings, leaderboard_enabled: v })}
                  data-testid="setting-leaderboard"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-5">
          <div className="space-y-4">
            {METHODS.map((m) => {
              const cfg = settings.payment_methods?.[m] || {};
              return (
                <div key={m} className="glass-strong rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-lg font-bold text-white uppercase">{m}</div>
                    <Switch checked={cfg.enabled !== false} onCheckedChange={(v) => setMethod(m, "enabled", v)} data-testid={`setting-method-${m}-enabled`} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Account Name" value={cfg.account_name || ""} onChange={(v) => setMethod(m, "account_name", v)} testid={`setting-${m}-name`} />
                    <Field label="Account Number" value={cfg.account_number || ""} onChange={(v) => setMethod(m, "account_number", v)} testid={`setting-${m}-number`} />
                    <div className="md:col-span-2">
                      <Field label="External URL (e.g. Saweria link)" value={cfg.url || ""} onChange={(v) => setMethod(m, "url", v)} testid={`setting-${m}-url`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-zinc-400">Instructions</Label>
                      <Textarea rows={2} value={cfg.instructions || ""} onChange={(e) => setMethod(m, "instructions", e.target.value)} className="mt-1 bg-black/40 border-white/10" data-testid={`setting-${m}-instructions`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-zinc-400">QR/Logo Image</Label>
                      <div className="flex items-center gap-3 mt-1">
                        {cfg.qr_image_url && <img src={fileUrl(cfg.qr_image_url)} alt="QR" className="w-20 h-20 object-cover rounded bg-white p-1" />}
                        <input type="file" accept="image/*" hidden ref={(el) => (qrInputs.current[m] = el)} onChange={(e) => uploadQR(m, e.target.files[0])} data-testid={`setting-${m}-qr-input`} />
                        <Button variant="outline" onClick={() => qrInputs.current[m]?.click()} data-testid={`setting-${m}-qr-upload`}><Upload className="w-4 h-4 mr-2" />Upload</Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-5">
          <div className="glass-strong rounded-xl p-6 max-w-2xl space-y-4">
            <div className="flex items-center gap-2 text-rose-300">
              <Mail className="w-5 h-5" />
              <div className="font-display text-lg font-bold text-white">SMTP Email Service</div>
            </div>
            <p className="text-sm text-zinc-400">
              Email notifications are sent for: new registrations, payment proof submissions, payment approvals/rejections,
              membership expiration reminders (7/3/1 days), password resets, and password changes.
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-black/40 border border-white/10">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Provider</div>
                <div className="text-white mt-1">Gmail SMTP</div>
              </div>
              <div className="p-3 rounded-lg bg-black/40 border border-white/10">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Sender Name</div>
                <div className="text-white mt-1">Kyouka Reimei TL</div>
              </div>
              <div className="p-3 rounded-lg bg-black/40 border border-white/10 col-span-2">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Admin Notifications Routed To</div>
                <div className="text-rose-300 mt-1 font-mono">yogasriadi123@gmail.com</div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="text-sm text-zinc-300 mb-2">Send a test email to verify SMTP is working.</div>
              <Button
                onClick={sendTestEmail}
                disabled={testingEmail}
                className="bg-rose-600 hover:bg-rose-500"
                data-testid="settings-test-email-btn"
              >
                {testingEmail
                  ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>)
                  : (<><Mail className="w-4 h-4 mr-2" /> Send Test Email</>)}
              </Button>
              <p className="text-xs text-zinc-500 mt-2 font-mystic italic">
                The test email is sent to the admin notification address. Check inbox & spam.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-5">
          <div className="glass-strong rounded-xl p-5 max-w-md space-y-4">
            <div className="text-sm text-zinc-400">Change your admin email and/or password. Current password is required.</div>
            <Field label="Current Password" type="password" value={creds.current_password} onChange={(v) => setCreds({ ...creds, current_password: v })} testid="setting-current-pwd" />
            <Field label="New Email (optional)" value={creds.new_email} onChange={(v) => setCreds({ ...creds, new_email: v })} testid="setting-new-email" />
            <Field label="New Password (optional)" type="password" value={creds.new_password} onChange={(v) => setCreds({ ...creds, new_password: v })} testid="setting-new-pwd" />
            <Button onClick={updateCredentials} className="bg-rose-600 hover:bg-rose-500" data-testid="setting-update-creds-btn"><KeyRound className="w-4 h-4 mr-2" />Update Credentials</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs text-zinc-400">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-black/40 border-white/10" data-testid={testid} />
    </div>
  );
}
